import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { omitThemingProps, useMultiStyleConfig, chakra, StylesProvider, useStyles } from '@chakra-ui/react';
import * as React from 'react';
import { getProgressProps, animation } from './utils.js';
var _excluded = ["min", "max", "value", "isIndeterminate"],
  _excluded2 = ["value", "min", "max", "children", "borderRadius", "isIndeterminate", "aria-label", "aria-labelledby"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

/**
 * ProgressFilledTrack
 *
 * The progress component that visually indicates the current level of the progress bar.
 * It applies `background-color` and changes its width.
 *
 * @see Docs https://chakra-ui.com/progress
 */
var ProgressFilledTrack = function ProgressFilledTrack(props) {
  var min = props.min,
    max = props.max,
    value = props.value,
    isIndeterminate = props.isIndeterminate,
    rest = _objectWithoutProperties(props, _excluded);
  var progress = getProgressProps({
    value: value,
    min: min,
    max: max,
    isIndeterminate: isIndeterminate
  });
  var styles = useStyles();
  var trackStyles = _objectSpread({
    height: '100%'
  }, styles.filledTrack);
  return /*#__PURE__*/React.createElement(chakra.div, _extends({
    style: _objectSpread({
      width: "".concat(progress.percent, "%")
    }, rest.style)
  }, progress.bind, rest, {
    __css: trackStyles
  }));
};

/**
 * Progress bar
 *
 * Progress is used to display the progress status for a task that takes a long
 * time or consists of several steps.
 *
 * It includes accessible attributes to help assistive technologies understand
 * and speak the progress values.
 *
 * @see Docs https://chakra-ui.com/progress
 */
var ProgressBar = function ProgressBar(props) {
  var _styles$track;
  var _omitThemingProps = omitThemingProps(props),
    value = _omitThemingProps.value,
    _omitThemingProps$min = _omitThemingProps.min,
    min = _omitThemingProps$min === void 0 ? 0 : _omitThemingProps$min,
    _omitThemingProps$max = _omitThemingProps.max,
    max = _omitThemingProps$max === void 0 ? 100 : _omitThemingProps$max,
    children = _omitThemingProps.children,
    propBorderRadius = _omitThemingProps.borderRadius,
    isIndeterminate = _omitThemingProps.isIndeterminate,
    ariaLabel = _omitThemingProps['aria-label'],
    ariaLabelledBy = _omitThemingProps['aria-labelledby'],
    rest = _objectWithoutProperties(_omitThemingProps, _excluded2);
  var styles = useMultiStyleConfig('ProgressBar', props);
  var borderRadius = propBorderRadius !== null && propBorderRadius !== void 0 ? propBorderRadius : (_styles$track = styles.track) === null || _styles$track === void 0 ? void 0 : _styles$track.borderRadius;
  /**
   * Generate styles for stripe and stripe animation
   */

  var css = _objectSpread({}, isIndeterminate && {
    position: 'absolute',
    willChange: 'left',
    minWidth: '50%',
    animation: "".concat(animation, " 1s ease infinite normal none running")
  });
  var trackStyles = _objectSpread({
    overflow: 'hidden',
    position: 'relative'
  }, styles.track);
  return /*#__PURE__*/React.createElement(chakra.div, _extends({
    borderRadius: borderRadius,
    __css: trackStyles
  }, rest), /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, /*#__PURE__*/React.createElement(ProgressFilledTrack, {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    min: min,
    max: max,
    value: value,
    isIndeterminate: isIndeterminate,
    css: css,
    borderRadius: borderRadius
  }), children));
};
export { ProgressBar };